import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  IconButton,
  Box,
  Chip,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Avatar,
  Zoom,
  Fade,
  Toolbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarToday,
  AccountBalance,
  TrendingUp,
  AttachMoney,
  Receipt,
  TableChart,
  DateRange,
  ViewColumn,
  MoreVert,
  Remove,
  Add
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const ExcelTable4 = ({ data, title }) => {
  const types = data ? [...new Set(data.map(row => row.type))] : [];
  const [currentPage, setCurrentPage] = React.useState(0);
  const [hoveredRow, setHoveredRow] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [compactMode, setCompactMode] = React.useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Dynamically calculate column width based on number of types
  const calculateColumnWidth = (totalTypes) => {
    // Base widths that decrease as types increase
    if (totalTypes <= 3) return { date: 100, type: 110, total: 95 };
    if (totalTypes <= 5) return { date: 90, type: 95, total: 85 };
    if (totalTypes <= 8) return { date: 85, type: 85, total: 80 };
    if (totalTypes <= 12) return { date: 80, type: 75, total: 75 };
    return { date: 75, type: 65, total: 70 };
  };

  const columnWidths = calculateColumnWidth(types.length);
  
  // Dynamic font size based on number of types
  const getFontSize = () => {
    if (types.length <= 3) return '0.7rem';
    if (types.length <= 5) return '0.65rem';
    if (types.length <= 8) return '0.6rem';
    return '0.55rem';
  };
  
  // Dynamic cell padding based on number of types
  const getCellPadding = () => {
    if (types.length <= 3) return 0.75;
    if (types.length <= 5) return 0.6;
    if (types.length <= 8) return 0.5;
    return 0.4;
  };

  const fontSize = getFontSize();
  const cellPadding = getCellPadding();

  const tableMinWidth = columnWidths.date + 
    (types.length * columnWidths.type) + 
    columnWidths.total;

  const { monthsData, initialPage, totals } = React.useMemo(() => {
    if (!data || data.length === 0) return { monthsData: [], initialPage: 0, totals: {} };

    const groupedByMonth = data.reduce((acc, row) => {
      const dateObj = new Date(row.date);
      const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      const dateKey = row.date;

      if (!acc[monthKey]) {
        acc[monthKey] = {
          monthKey,
          dates: {},
          total: 0,
          typeTotals: {}
        };
      }

      if (!acc[monthKey].dates[dateKey]) {
        acc[monthKey].dates[dateKey] = {
          date: row.date,
          values: {},
          total: 0
        };
      }

      const type = row.type;
      acc[monthKey].dates[dateKey].values[type] = (acc[monthKey].dates[dateKey].values[type] || 0) + row.value;
      acc[monthKey].dates[dateKey].total += row.value;
      acc[monthKey].total += row.value;
      acc[monthKey].typeTotals[type] = (acc[monthKey].typeTotals[type] || 0) + row.value;

      return acc;
    }, {});

    // Calculate overall totals
    const overallTotals = data.reduce((acc, row) => {
      acc[row.type] = (acc[row.type] || 0) + row.value;
      acc.total = (acc.total || 0) + row.value;
      return acc;
    }, {});

    const monthsData = Object.values(groupedByMonth)
      .sort((a, b) => new Date(b.monthKey) - new Date(a.monthKey))
      .map(month => ({
        ...month,
        dates: Object.values(month.dates).sort((a, b) => new Date(b.date) - new Date(a.date))
      }));

    const currentDate = new Date();
    const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    const initialPage = monthsData.findIndex(m => m.monthKey === currentMonthKey);

    return { 
      monthsData, 
      initialPage: initialPage !== -1 ? initialPage : 0,
      totals: overallTotals
    };
  }, [data]);

  React.useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  const formatMonth = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, monthsData.length - 1));
  };

  const formatNumber = num => 
    new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'TND', 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num || 0);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleCompactMode = () => {
    setCompactMode(!compactMode);
    handleMenuClose();
  };

  // Determine which types to show in the month summary (limit based on space)
  const visibleTypesInSummary = types.length <= 4 ? types : types.slice(0, 3);
  const hiddenTypesCount = types.length - visibleTypesInSummary.length;

  if (!data || data.length === 0) {
    return (
      <Fade in={true}>
        <Card sx={{ 
          borderRadius: 2,
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          boxShadow: 'none'
        }}>
          <CardContent sx={{ p: 2, textAlign: 'center' }}>
            <TableChart sx={{ fontSize: 40, color: '#B0BEC5', mb: 1 }} />
            <Typography variant="body2" sx={{ color: '#5F6B7A', fontWeight: 500 }}>
              Aucune donnée disponible
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    );
  }

  return (
    <Box>
      {/* Compact Header with Month Navigation */}
      <Card sx={{ 
        mb: 2,
        borderRadius: 2,
        background: '#FFFFFF',
        border: '1px solid #E0E0E0',
        boxShadow: 'none'
      }}>
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: 'linear-gradient(135deg, #2E7D32, #4CAF50)',
                }}
              >
                <Receipt sx={{ fontSize: 18, color: '#FFFFFF' }} />
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ color: '#1A2B3C', fontWeight: 600 }}>
                  {title || 'Encaissements'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9AA6B2', fontSize: '0.65rem' }}>
                  {types.length} types • {data.length} entrées
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Options d'affichage">
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{ width: 28, height: 28 }}
                >
                  <MoreVert sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DateRange sx={{ color: '#9AA6B2', fontSize: 14 }} />
                <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 600, fontSize: '0.8rem' }}>
                  {formatMonth(monthsData[currentPage]?.monthKey)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0}
                  size="small"
                  sx={{
                    bgcolor: currentPage === 0 ? '#F5F5F5' : '#E8F5E9',
                    color: currentPage === 0 ? '#9AA6B2' : '#2E7D32',
                    width: 28,
                    height: 28,
                    '&:hover': {
                      bgcolor: '#C8E6C9',
                    }
                  }}
                >
                  <ChevronLeft sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  onClick={handleNextPage}
                  disabled={currentPage === monthsData.length - 1}
                  size="small"
                  sx={{
                    bgcolor: currentPage === monthsData.length - 1 ? '#F5F5F5' : '#E8F5E9',
                    color: currentPage === monthsData.length - 1 ? '#9AA6B2' : '#2E7D32',
                    width: 28,
                    height: 28,
                    '&:hover': {
                      bgcolor: '#C8E6C9',
                    }
                  }}
                >
                  <ChevronRight sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Display Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }
        }}
      >
        <MenuItem onClick={toggleCompactMode}>
          <ListItemIcon>
            {compactMode ? <Add fontSize="small" /> : <Remove fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {compactMode ? 'Mode normal' : 'Mode compact'}
          </ListItemText>
          <Typography variant="caption" sx={{ color: '#9AA6B2', ml: 1 }}>
            {compactMode ? 'Activer' : 'Désactiver'}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem disabled>
          <ListItemIcon>
            <ViewColumn fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={`${types.length} colonnes de types`} />
          <Typography variant="caption" sx={{ color: '#2E7D32' }}>
            Auto-ajusté
          </Typography>
        </MenuItem>
      </Menu>

      {/* Compact Table Container */}
      <Card sx={{
        borderRadius: 2,
        border: '1px solid #E0E0E0',
        boxShadow: 'none',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          maxWidth: '100%',
          overflowX: 'auto',
          position: 'relative',
          '&::-webkit-scrollbar': {
            height: 4,
          }
        }}>
          <TableContainer sx={{ minWidth: tableMinWidth }}>
            <Table 
              size="small"
              sx={{
                tableLayout: 'fixed',
                '& .MuiTableCell-root': {
                  py: cellPadding,
                  px: compactMode ? 0.5 : 1,
                  fontSize: fontSize,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  borderBottom: '1px solid #F0F0F0',
                }
              }}
            >
              {/* Table Head */}
              <TableHead>
                {/* Month Total Row - Compact */}
                <TableRow>
                  <TableCell 
                    colSpan={1 + types.length + 1}
                    sx={{
                      bgcolor: '#FAFAFA',
                      borderBottom: '1px solid #E0E0E0',
                      py: 0.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<TrendingUp sx={{ fontSize: compactMode ? 12 : 14 }} />}
                        label={`Total: ${formatNumber(monthsData[currentPage]?.total || 0)}`}
                        size="small"
                        sx={{
                          height: compactMode ? 20 : 24,
                          fontSize: compactMode ? '0.6rem' : '0.65rem',
                          bgcolor: '#E8F5E9',
                          color: '#2E7D32',
                          '& .MuiChip-icon': { fontSize: compactMode ? 12 : 14 }
                        }}
                      />
                      {visibleTypesInSummary.map(type => (
                        <Chip
                          key={type}
                          size="small"
                          label={`${type}: ${formatNumber(monthsData[currentPage]?.typeTotals[type] || 0)}`}
                          sx={{
                            height: compactMode ? 20 : 24,
                            fontSize: compactMode ? '0.55rem' : '0.6rem',
                            bgcolor: '#F5F5F5',
                            color: '#5F6B7A',
                          }}
                        />
                      ))}
                      {hiddenTypesCount > 0 && (
                        <Chip
                          size="small"
                          label={`+${hiddenTypesCount} autres`}
                          sx={{
                            height: compactMode ? 20 : 24,
                            fontSize: compactMode ? '0.55rem' : '0.6rem',
                            bgcolor: '#F5F5F5',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Header Row - Compact */}
                <TableRow>
                  <TableCell 
                    align="left"
                    sx={{
                      width: columnWidths.date,
                      minWidth: columnWidths.date,
                      maxWidth: columnWidths.date,
                      fontWeight: 600,
                      background: '#F5F7FA',
                      color: '#1A2B3C',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      borderRight: '1px solid #E0E0E0',
                      py: cellPadding,
                      fontSize: fontSize
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: compactMode ? 10 : 12 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: fontSize }}>
                        Date
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  {types.map((type) => (
                    <TableCell 
                      key={type} 
                      align="center"
                      sx={{
                        width: columnWidths.type,
                        minWidth: columnWidths.type,
                        maxWidth: columnWidths.type,
                        fontWeight: 600,
                        background: '#FAFAFA',
                        color: '#1A2B3C',
                        borderRight: '1px solid #E0E0E0',
                        py: cellPadding,
                        fontSize: fontSize
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <AccountBalance sx={{ fontSize: compactMode ? 10 : 12, color: '#2E7D32' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: fontSize }}>
                          {type}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}

                  <TableCell 
                    align="center" 
                    sx={{ 
                      width: columnWidths.total,
                      minWidth: columnWidths.total,
                      maxWidth: columnWidths.total,
                      fontWeight: 600,
                      background: '#2E7D32',
                      color: '#FFFFFF',
                      py: cellPadding,
                      fontSize: fontSize
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <AttachMoney sx={{ fontSize: compactMode ? 10 : 12 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: fontSize }}>
                        Total
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* Table Body */}
              <TableBody>
                <AnimatePresence>
                  {monthsData[currentPage]?.dates.slice(0, compactMode ? 6 : 8).map((dateRow, index) => (
                    <motion.tr
                      key={dateRow.date}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: index * 0.02 }}
                      onMouseEnter={() => setHoveredRow(dateRow.date)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{
                        backgroundColor: hoveredRow === dateRow.date 
                          ? alpha('#E8F5E9', 0.3)
                          : 'transparent',
                      }}
                    >
                      <TableCell 
                        align="left"
                        sx={{
                          width: columnWidths.date,
                          minWidth: columnWidths.date,
                          maxWidth: columnWidths.date,
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          bgcolor: hoveredRow === dateRow.date ? alpha('#E8F5E9', 0.3) : '#FFFFFF',
                          borderRight: '1px solid #E0E0E0',
                          fontWeight: 400,
                          fontSize: fontSize,
                          py: cellPadding
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday sx={{ fontSize: compactMode ? 8 : 10, color: '#9AA6B2' }} />
                          {new Date(dateRow.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit'
                          })}
                        </Box>
                      </TableCell>

                      {types.map((type) => {
                        const value = dateRow.values[type] || 0;
                        return (
                          <TableCell 
                            key={type} 
                            align="right"
                            sx={{
                              width: columnWidths.type,
                              minWidth: columnWidths.type,
                              maxWidth: columnWidths.type,
                              borderRight: '1px solid #E0E0E0',
                              color: value > 0 ? '#2E7D32' : '#BDBDBD',
                              fontWeight: value > 0 ? 500 : 400,
                              fontSize: fontSize,
                              py: cellPadding
                            }}
                          >
                            {value > 0 ? value.toFixed(2) : '-'}
                          </TableCell>
                        );
                      })}

                      <TableCell 
                        align="right"
                        sx={{
                          width: columnWidths.total,
                          minWidth: columnWidths.total,
                          maxWidth: columnWidths.total,
                          fontWeight: 600,
                          color: '#1A2B3C',
                          fontSize: fontSize,
                          py: cellPadding
                        }}
                      >
                        {dateRow.total.toFixed(2)}
                      </TableCell>
                    </motion.tr>
                  ))}
                </AnimatePresence>

                {/* Grand Total Row - Compact */}
                <TableRow sx={{ bgcolor: '#FAFAFA', borderTop: '1px solid #E0E0E0' }}>
                  <TableCell 
                    colSpan={1}
                    sx={{ 
                      fontWeight: 600,
                      color: '#1A2B3C',
                      fontSize: fontSize,
                      py: cellPadding
                    }}
                  >
                    Total Général
                  </TableCell>
                  
                  {types.map((type) => (
                    <TableCell 
                      key={type} 
                      align="right"
                      sx={{ 
                        fontWeight: 600,
                        color: '#2E7D32',
                        borderRight: '1px solid #E0E0E0',
                        fontSize: fontSize,
                        py: cellPadding
                      }}
                    >
                      {(totals[type] || 0).toFixed(2)}
                    </TableCell>
                  ))}

                  <TableCell 
                    align="right"
                    sx={{ 
                      fontWeight: 700,
                      color: '#FFFFFF',
                      background: '#2E7D32',
                      fontSize: fontSize,
                      py: cellPadding
                    }}
                  >
                    {(totals.total || 0).toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Compact Footer Stats */}
        <Box sx={{ 
          p: compactMode ? 0.75 : 1,
          background: '#FAFAFA',
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={<Receipt sx={{ fontSize: compactMode ? 12 : 14 }} />}
              label={`${data.length} transactions`}
              size="small"
              sx={{ height: compactMode ? 20 : 24, fontSize: compactMode ? '0.6rem' : '0.65rem', bgcolor: '#FFFFFF' }}
            />
            <Chip
              icon={<AccountBalance sx={{ fontSize: compactMode ? 12 : 14 }} />}
              label={`${types.length} types`}
              size="small"
              sx={{ height: compactMode ? 20 : 24, fontSize: compactMode ? '0.6rem' : '0.65rem', bgcolor: '#FFFFFF' }}
            />
            {types.length > 8 && (
              <Chip
                label="Tableau compact"
                size="small"
                sx={{ height: compactMode ? 20 : 24, fontSize: compactMode ? '0.55rem' : '0.6rem', bgcolor: '#E3F2FD', color: '#1976D2' }}
              />
            )}
          </Box>
          
          <Typography variant="caption" sx={{ color: '#9AA6B2', fontSize: '0.55rem' }}>
            {new Date().toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        </Box>
      </Card>

      {/* Info Banner when many types */}
      {types.length > 10 && (
        <Box sx={{ 
          mt: 1, 
          p: 0.5, 
          bgcolor: alpha('#FF9800', 0.1),
          borderRadius: 1,
          textAlign: 'center'
        }}>
          <Typography variant="caption" sx={{ color: '#ED6C02', fontSize: '0.6rem' }}>
            💡 {types.length} types de transactions - Tableau optimisé pour l'affichage compact
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ExcelTable4);